/**
 * @module @app/shared/files/parser/common/file
 * @description Common file parser. Commonly used to verify file size,
 * type and return corresponding file content.
 */
import { Err, Ok } from "../../../lib/patterns/result";
import type { Parser, ParsingError, ParsingRule } from "../types";

export enum FILE_PARSING_ERROR {
  FILE_EMPTY = "FILE_EMPTY",
  FILE_SIZE_LIMIT_EXCEEDED = "FILE_SIZE_LIMIT_EXCEEDED",
  FILE_INVALID_TYPE = "FILE_INVALID_TYPE",
}

export const MAX_SIZE_MODEL_IMAGE = 20971520; // 20 Mo in bytes

/**
 * @description Check if a file is empty.
 */
const hasEmptyFile: ParsingRule = (file: File) => file.size === 0;

/**
 * @description Check if a file size is greater than 20mb.
 */
const hasInvalidFileSize: ParsingRule = (file: File) => file.size > MAX_SIZE_MODEL_IMAGE;

const hasInvalidFileType: ParsingRule = (file: File, expectedMimeTypes: string | string[]) => {
  const mimeTypes = Array.isArray(expectedMimeTypes) ? expectedMimeTypes : [expectedMimeTypes];
  return !mimeTypes.includes(file.type);
};

/**
 * Parse a file and return its content as a string.
 * If an error occurred, return a ParsingError.
 * Perform:
 * - file size check between 0 and 20mb
 * - file type check
 *
 * @async
 * @param file The file to parse
 * @param expectedMimeType Expected mime type of the file
 * @returns File content as a string if no error occurred. Otherwise, return a ParsingError.
 */
export const parseFile: Parser<
  string,
  ParsingError<FILE_PARSING_ERROR>,
  { expectedMimeType: string | string[] }
> = async (file, { expectedMimeType }) => {
  if (hasEmptyFile(file))
    return Err({
      kind: FILE_PARSING_ERROR.FILE_EMPTY,
      explain: "file is empty",
      location: { file: file.name, line: 0 },
      data: {},
    });

  if (hasInvalidFileSize(file))
    return Err({
      kind: FILE_PARSING_ERROR.FILE_SIZE_LIMIT_EXCEEDED,
      explain: "file size limit exceeded",
      location: { file: file.name, line: 0 },
      data: { hint: `${file.size / 1048576}mb` },
    });

  if (hasInvalidFileType(file, expectedMimeType))
    return Err({
      kind: FILE_PARSING_ERROR.FILE_INVALID_TYPE,
      explain: `file type don't correspond to expected type.`,
      location: { file: file.name, line: 0 },
      data: {
        value: file.type,
      },
    });

  return Ok(await file.text());
};
